/* eslint-disable no-undef */
/*
 *
 * Websocket
 *
 */

import { useEffect, memo, useState, useRef, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import selectWebsocket from './selectors';
import pako from 'pako';
import PropTypes from 'prop-types';
import reducer from './reducer';
import saga from './saga';
import { useInjectSaga } from '../../../config/utils/injectSaga';
import { useInjectReducer } from '../../../config/utils/injectReducer';
import { cltStatusReceived, updateComponent, vncClientListReceived, ibaStatusReceived, editControlLockListReceived, scannerStatusReceived, scannerStatusChanged, sendToWs } from './actions';
import { indicatorChange } from '../Sidebar/actions';
import { makeSelectArchiveMode, makeSelectDebugMode } from '../App/selectors';
import { Buffer } from 'buffer';
import { showMessageToClient } from '../WebsocketMessageModal/actions';
import { showGlobalMessage, clearGlobalMessage } from '../GlobalMessageModal/actions';
import { useRefreshDates } from './RefreshDatesProvider';
import axios from 'axios';
import auth from '../../../config/utils/auth';
import { updateClientName, updateClientIP } from '../Header/actions';
import { updateAppHash } from '../App/actions';
import IdentifyFrame from '../../components/IdentifyFrame';
import mergeCltStatus, { compactCltStatusHashes } from './diffCltStatus';
import { updatePerformanceContext, startPerformanceMeasurement, recordPerformanceMeasurement } from './cltStatusPerformance';
import { addModal } from '../ModalManager/actions';
import _ from 'lodash';
import { useControlFeedback } from '../../components/ControlFeedbackSnackbar/ControlFeedbackContext';
import ControlFeedbackContainer from '../../components/ControlFeedbackSnackbar/ControlFeedbackContainer';
import { clearDebugSnapshotRequest, takeDebugSnapshotRequest } from './debugSnapshotRequest';

const FULL_REQUEST_RETRY_MS = 5000;

export function Websocket(props) {
  // eslint-disable-line react/prefer-stateless-function
  // console.log('Websocket: ', props.websocket.websocket.cltstatus.map);
  // console.log('websocket props login', props?.websocket?.app?.loginState?.user?.username);
  useInjectReducer({ key: 'websocket', reducer });
  useInjectSaga({ key: 'websocket', saga });

  const dispatch = useDispatch();
  const { refreshDatesRef, updateRefreshDates } = useRefreshDates();
  const { showFeedback } = useControlFeedback();

  const [address, setAddress] = useState('');
  const [user, setUser] = useState('');
  // const [appHash, setAppHash] = useState('');
  const [identifyActive, setIdentifyActive] = useState(false);
  const [identifyColor, setIdentifyColor] = useState('#FFD200');
  const identifyTimerRef = useRef(null);
  const latestCltStatusRef = useRef(props?.websocket?.websocket?.latestCltstatus || null);
  const lastTickRef = useRef(Number(props?.websocket?.websocket?.latestCltstatus?.cycleData?.tick) || 0);
  const awaitingFullResyncRef = useRef(false);
  const forceFullResyncRef = useRef(false);
  const hasCompactSnapshotRef = useRef(false);
  const fullSnapshotRetryRef = useRef(null);
  const cltWorkerRef = useRef(null);
  const cltWorkerEpochRef = useRef(0);
  const wasArchiveModeRef = useRef(props.archiveMode);
  const wasDebugModeRef = useRef(props.debugMode);

  useEffect(() => {
    const ws = props.websocket.websocket.websocketObject;

    ws.onopen = onWebsocketOpen;
    ws.onclose = onWebsocketClose;

    if (ws.readyState === 1) {
      onWebsocketOpen();
    }

    return () => {
      if (ws.onopen === onWebsocketOpen) ws.onopen = null;
      if (ws.onclose === onWebsocketClose) ws.onclose = null;
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    const ip = new Promise((resolve, reject) => {
      const conn = new RTCPeerConnection();
      conn.createDataChannel('');
      conn.createOffer((offer) => conn.setLocalDescription(offer), reject);
      conn.onicecandidate = (ice) => {
        if (ice && ice.candidate && ice.candidate.candidate) {
          try {
            // console.log("HERE?", ice.candidate.candidate);
            resolve(ice.candidate.candidate.split(' ')[4]);
            const myIP = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/.exec(ice.candidate.candidate)[1];
            setAddress(myIP);
            dispatch(updateClientIP(myIP));
            conn.close();
          } catch (error) {
            setAddress(ice.candidate.candidate);
          }
        } else {
          setAddress('UNKNOWN');
        }
      };
    });
  }, []);

  useEffect(() => {
    setUser(props?.websocket?.app?.loginState?.user?.username);
  }, [props?.websocket?.app?.loginState?.user?.username]);

  useEffect(() => {
    let token;
    const interval = setInterval(() => {
      token = auth.getToken();

      if (token != null) {
        axios
          .post(
            `/api/v1/vnc/xlamclients/terminalPing/`,
            { ip: address, user },
            {
              headers: {
                Authorization: 'Bearer ' + auth.getToken()
              }
            }
          )
          .then((response) => {
            const data = JSON.parse(response.data);
            dispatch(updateClientName(data.clientName));
            dispatch(updateAppHash(data.hash));

            dispatch(sendToWs({ ip: address }, 'client-ping'));
          })
          .catch((error) => console.error(error));
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [address]);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = auth.getToken();
      // Fetch all refresh times
      if (token != null) {
        axios
          .get(`/api/v1/productionline/refreshTime/`, {
            headers: {
              Authorization: 'Bearer ' + auth.getToken()
            }
          })
          .then((response) => {
            if (response.status !== 200) {
              throw new Error('Network response was not ok');
            } else {
              const results = _.get(response, 'data.results', []);
              results.map((result) => {
                const { identifier, date } = result;
                const newDate = new Date(date);
                const currentStoredDate = new Date(refreshDatesRef.current[identifier] || 0);

                if (currentStoredDate == 0) {
                  updateRefreshDates(identifier, newDate);
                } else if (newDate > currentStoredDate) {
                  dispatch(updateComponent('refresh-view', { api: identifier }));
                  updateRefreshDates(identifier, newDate);
                }
              });
            }
          })
          .catch((error) => {
            console.error('There was a problem with the fetch operation:', error);
          });
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchEditControlLocks = () => {
      const token = auth.getToken();
      if (token != null) {
        axios
          .get('/api/v1/productionline/editControlLocks/', {
            headers: {
              Authorization: 'Bearer ' + token
            }
          })
          .then((response) => {
            const list = _.get(response, 'data.results', response?.data || []);
            dispatch(editControlLockListReceived(list));
          })
          .catch((error) => {
            console.error('Failed to fetch edit control locks:', error);
          });
      }
    };

    fetchEditControlLocks();
    const interval = setInterval(fetchEditControlLocks, 2000);
    return () => clearInterval(interval);
  }, [dispatch]);

  function onWebsocketOpen() {
    // console.log('onWebsocketOpen ');
    dispatch(indicatorChange('backend', true));
    if (!wasArchiveModeRef.current) requestFullSnapshot(!hasCompactSnapshotRef.current);
  }

  function onWebsocketClose() {
    clearFullSnapshotRequest();
    dispatch(indicatorChange('backend', false));
  }

  const handleAdditionalVariables = useCallback(
    (injectedData) => {
      if (injectedData.globalMessage) {
        dispatch(showGlobalMessage(injectedData.globalMessage));
      } else {
        dispatch(clearGlobalMessage());
      }
    },
    [dispatch]
  );

  const addressRef = useRef('');
  useEffect(() => {
    addressRef.current = address;
  }, [address]);

  useEffect(() => {
    latestCltStatusRef.current = props?.websocket?.websocket?.latestCltstatus || null;
  }, [props?.websocket?.websocket?.latestCltstatus]);

  const clearFullSnapshotRequest = useCallback(() => {
    awaitingFullResyncRef.current = false;
    forceFullResyncRef.current = false;
    updatePerformanceContext({ pending: false });
    if (fullSnapshotRetryRef.current) {
      window.clearInterval(fullSnapshotRetryRef.current);
      fullSnapshotRetryRef.current = null;
    }
  }, []);

  const requestFullSnapshot = useCallback(
    (forceFull = false) => {
      if (awaitingFullResyncRef.current) return;

      if (forceFull && cltWorkerRef.current) {
        cltWorkerEpochRef.current += 1;
        cltWorkerRef.current.postMessage({ type: 'reset', epoch: cltWorkerEpochRef.current });
      }
      const sendRequest = () => dispatch(sendToWs({ lastTick: forceFull ? 0 : lastTickRef.current }, 'request-clt-status-snapshot'));
      awaitingFullResyncRef.current = true;
      forceFullResyncRef.current = forceFull;
      updatePerformanceContext({ pending: true });
      sendRequest();
      fullSnapshotRetryRef.current = window.setInterval(sendRequest, FULL_REQUEST_RETRY_MS);
    },
    [dispatch]
  );

  useEffect(() => {
    updatePerformanceContext({
      archiveMode: !!props.archiveMode,
      debugMode: !!props.debugMode
    });
  }, [props.archiveMode, props.debugMode]);

  useEffect(() => {
    if (props.archiveMode) {
      clearFullSnapshotRequest();
    } else if (wasArchiveModeRef.current) {
      lastTickRef.current = 0;
      clearFullSnapshotRequest();
      requestFullSnapshot();
    } else if (wasDebugModeRef.current && !props.debugMode) {
      clearDebugSnapshotRequest();
      clearFullSnapshotRequest();
      requestFullSnapshot(true);
    }
    wasArchiveModeRef.current = props.archiveMode;
    wasDebugModeRef.current = props.debugMode;
  }, [props.archiveMode, props.debugMode, clearFullSnapshotRequest, requestFullSnapshot]);

  useEffect(() => () => clearFullSnapshotRequest(), [clearFullSnapshotRequest]);

  const decodeCltStatusPayload = useCallback((encoded, isFullStatus = false, measurement) => {
    const start = Date.now();
    let phaseStart = performance.now();
    const buffer = Buffer.from(encoded, 'base64');
    if (measurement) measurement.base64_ms += performance.now() - phaseStart;
    phaseStart = performance.now();
    const output = pako.inflate(buffer, { to: 'string' });
    if (measurement) measurement.decompress_ms += performance.now() - phaseStart;
    phaseStart = performance.now();
    const ws = JSON.parse(output);
    if (measurement) {
      measurement.parse_ms += performance.now() - phaseStart;
      measurement.diff_bytes += buffer.length;
      measurement.changed_indices += Object.entries(ws.map || {}).reduce((count, [key, value]) => count + (key.endsWith('DiffIndices') && Array.isArray(value) ? value.length : 0), 0);
    }
    ws.cycleData = {
      inflateCycle: Date.now() - start,
      originalSize: output.length,
      compressedSize: buffer.length,
      fullStatus: !!isFullStatus
    };
    return ws;
  }, []);

  const applyCltStatus = useCallback(
    (status, tick, publicationAgeMs, measurement) => {
      const appliedStatus = {
        ...status,
        cycleData: {
          ...(status.cycleData || {}),
          tick,
          publication_age_ms: Number.isFinite(publicationAgeMs) ? publicationAgeMs : null,
          receivedAt: performance.now(),
          archiveModeStep: _.get(status, 'map.globalSettings.archiveIntervalStep', 60000)
        }
      };
      latestCltStatusRef.current = appliedStatus;
      lastTickRef.current = tick;
      const dispatchStarted = performance.now();
      dispatch(cltStatusReceived(appliedStatus));
      if (measurement) {
        measurement.dispatch_ms = performance.now() - dispatchStarted;
        recordPerformanceMeasurement(measurement, tick);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (props.archiveMode || typeof Worker === 'undefined') return undefined;
    let worker;
    try {
      worker = new Worker(new URL('./cltStatus.worker.js', import.meta.url));
      cltWorkerRef.current = worker;
      cltWorkerEpochRef.current += 1;
      worker.postMessage({ type: 'init', epoch: cltWorkerEpochRef.current, snapshot: latestCltStatusRef.current, tick: lastTickRef.current });
    } catch (error) {
      worker?.terminate();
      cltWorkerRef.current = null;
      console.error('CLT status worker unavailable; using main-thread processing:', error);
      return undefined;
    }

    worker.onmessage = ({ data }) => {
      if (cltWorkerRef.current !== worker || data.epoch !== cltWorkerEpochRef.current) return;
      if (data.type === 'resync') {
        console.error('CLT status worker requested a full resync:', data.error);
        clearFullSnapshotRequest();
        requestFullSnapshot(true);
        return;
      }
      if (data.type !== 'status') return;
      try {
        const prepareStarted = performance.now();
        const merged = data.replace ? data.patch : mergeCltStatus(latestCltStatusRef.current, data.patch);
        const measurement = { ...data.measurement, startedAt: prepareStarted - data.processingMs, merge_ms: data.measurement.merge_ms + performance.now() - prepareStarted };
        const status = { ...merged, cycleData: { ...(merged.cycleData || {}), workerProcessed: true, coalescedStatuses: data.processedStatuses } };
        const publicationAgeMs = Number.isFinite(data.publicationAgeMs) ? data.publicationAgeMs + Math.max(0, Date.now() - data.receivedAt) : data.publicationAgeMs;
        hasCompactSnapshotRef.current = data.compact;
        applyCltStatus(status, data.tick, publicationAgeMs, measurement);
        if (data.resyncComplete) clearFullSnapshotRequest();
        if (data.additionalVariables) handleAdditionalVariables(data.additionalVariables);
        worker.postMessage({ type: 'ack', epoch: data.epoch });
      } catch (error) {
        console.error('Failed to publish CLT worker status:', error);
        clearFullSnapshotRequest();
        requestFullSnapshot(true);
      }
    };
    worker.onerror = (error) => {
      if (cltWorkerRef.current !== worker) return;
      console.error('CLT status worker failed; using main-thread processing:', error.message);
      worker.terminate();
      cltWorkerRef.current = null;
      clearFullSnapshotRequest();
      requestFullSnapshot(true);
    };
    return () => {
      if (cltWorkerRef.current === worker) cltWorkerRef.current = null;
      worker.terminate();
    };
  }, [props.archiveMode, props.debugMode, applyCltStatus, clearFullSnapshotRequest, requestFullSnapshot, handleAdditionalVariables]);

  const onMessage = useCallback(
    (msg) => {
      const status = JSON.parse(msg.data);
      if (cltWorkerRef.current && (status.type === 'clt-status' || status.type === 'clt-status-replay')) {
        if (props.archiveMode) return;
        const isReplay = status.type === 'clt-status-replay';
        const tick = Number(status.tick) || 0;
        const isFull = !isReplay && (status.fullStatus === true || (status.fullStatus == null && tick === 0));
        if (isReplay && (props.debugMode || forceFullResyncRef.current)) return;
        if (props.debugMode && (!isFull || !takeDebugSnapshotRequest())) return;
        cltWorkerRef.current.postMessage({ type: 'status', status, debugMode: props.debugMode, epoch: cltWorkerEpochRef.current, receivedAt: Date.now() });
        return;
      }
      if (status.type === 'clt-status-replay') {
        if (props.archiveMode) {
          return;
        }
        if (props.debugMode) {
          return;
        }

        if (forceFullResyncRef.current) return;

        try {
          const measurement = startPerformanceMeasurement('replay');
          const fromTick = Number(status.fromTick);
          const toTick = Number(status.toTick);
          const diffs = status.diffs;
          const currentTick = lastTickRef.current;
          const previousStatus = latestCltStatusRef.current;

          if (!previousStatus || currentTick < fromTick || diffs.length !== toTick - fromTick) {
            throw new Error('Invalid CLT status replay');
          }

          let mergedStatus = previousStatus;
          let replayedDiffs = 0;
          let originalSize = 0;
          let compressedSize = 0;
          const replayStart = Date.now();
          diffs.forEach((entry, index) => {
            const replayTick = Number(entry.tick);
            if (replayTick !== fromTick + index + 1) {
              throw new Error('Invalid CLT status replay');
            }
            if (replayTick > currentTick) {
              const diff = decodeCltStatusPayload(entry.data, false, measurement);
              replayedDiffs += 1;
              originalSize += diff.cycleData.originalSize;
              compressedSize += diff.cycleData.compressedSize;
              const mergeStarted = performance.now();
              mergedStatus = mergeCltStatus(mergedStatus, diff);
              measurement.merge_ms += performance.now() - mergeStarted;
            }
          });

          const appliedTick = Math.max(currentTick, toTick);
          mergedStatus = {
            ...mergedStatus,
            cycleData: {
              ...(mergedStatus.cycleData || {}),
              fullStatus: false,
              replayedDiffs,
              ...(replayedDiffs > 0 && {
                inflateCycle: Date.now() - replayStart,
                originalSize,
                compressedSize
              })
            }
          };
          applyCltStatus(mergedStatus, appliedTick, status.publication_age_ms, measurement);
          clearFullSnapshotRequest();
        } catch (error) {
          console.error('Failed to apply CLT status replay:', error);
          lastTickRef.current = 0;
          clearFullSnapshotRequest();
          requestFullSnapshot();
        }
      } else if (status.type == 'clt-status') {
        if (props.archiveMode) {
          return;
        }

        const currentTick = Number(status.tick) || 0;
        const isFullStatus = status.fullStatus === true || (status.fullStatus == null && currentTick === 0);
        if (props.debugMode && (!isFullStatus || !takeDebugSnapshotRequest())) {
          return;
        }

        if (isFullStatus) {
          let ws;
          const measurement = startPerformanceMeasurement(props.debugMode ? 'debug' : 'full');
          try {
            ws = decodeCltStatusPayload(status.data, true, measurement);
            if (!props.debugMode) {
              const prepareStarted = performance.now();
              ws = compactCltStatusHashes(ws);
              measurement.prepare_hashes_ms = performance.now() - prepareStarted;
            }
            hasCompactSnapshotRef.current = !props.debugMode;
          } catch (error) {
            console.error('Failed to decode full CLT status:', error);
            requestFullSnapshot();
            return;
          }
          applyCltStatus(ws, currentTick, status.publication_age_ms, measurement);
          clearFullSnapshotRequest();
          if (status.additionalVariables) {
            handleAdditionalVariables(status.additionalVariables);
          }
          return;
        }

        if (awaitingFullResyncRef.current) {
          return;
        }

        const previousStatus = latestCltStatusRef.current;
        const previousTick = lastTickRef.current;
        if (!previousStatus || previousTick <= 0 || currentTick <= 0) {
          requestFullSnapshot();
          return;
        }

        if (currentTick <= previousTick) {
          return;
        }

        if (currentTick !== previousTick + 1) {
          requestFullSnapshot();
          return;
        }

        let mergedStatus;
        const measurement = startPerformanceMeasurement('diff');
        try {
          const diff = decodeCltStatusPayload(status.data, false, measurement);
          const mergeStarted = performance.now();
          mergedStatus = mergeCltStatus(previousStatus, diff);
          measurement.merge_ms = performance.now() - mergeStarted;
        } catch (error) {
          console.error('Failed to apply CLT status diff:', error);
          requestFullSnapshot();
          return;
        }
        applyCltStatus(mergedStatus, currentTick, status.publication_age_ms, measurement);
        if (status.additionalVariables) {
          handleAdditionalVariables(status.additionalVariables);
        }
      } else if (status.type === 'vnc-client-list') {
        dispatch(vncClientListReceived(status.data));
      } else if (status.type === 'iba-status') {
        try {
          const ws = decodeCltStatusPayload(status.data, true);
          dispatch(ibaStatusReceived(ws));
        } catch (error) {}
      } else if (status.type === 'edit-control-lock-list') {
        dispatch(editControlLockListReceived(status.data));
      } else if (status.type === 'scanner-status') {
        dispatch(scannerStatusReceived());
        dispatch(scannerStatusChanged());
      } else if (status.type === 'update-component') {
        let componentPayload = status.data;
        if (typeof componentPayload === 'string') {
          try {
            componentPayload = JSON.parse(componentPayload);
          } catch (e) {
            componentPayload = null;
          }
        }

        const componentName = _.get(componentPayload, 'componentName', null);
        const componentObject = _.get(componentPayload, 'object', null);
        if (componentName) {
          dispatch(updateComponent(componentName, componentObject));
        }
      } else if (status.type === 'extra-lamella-produced') {
        this.props.refreshView('extralamellas');
      } /* else if (status.type === 'refresh-view') {
      refreshViewWithDates(status.data.componentName, status.data.object, status.data.date);
    } */ else if (status.type == 'broadcast_message') {
        dispatch(showMessageToClient(status.data));
      } else if (status.type == 'solution-options') {
        const parsedData = JSON.parse(status.data);
        const { solutions, uuid, validations } = parsedData;
        dispatch(solutionOptionsRecieved(solutions, uuid, validations));
      } else if (status.type == 'last-response') {
        const parsedData = JSON.parse(status.data);
        const { lastResponse, uuid } = parsedData;
        dispatch(lastResponseRecieved(lastResponse, uuid));
      } else if (status.type === 'identify-terminal') {
        console.log('identify msg ip:', status.ip, 'my ip:', addressRef.current);
        if (status.ip && status.ip === addressRef.current) {
          const dur = Math.max(1, Number(status.durationMs) || 8000);
          const col = status.color || '#FFD200';
          if (identifyTimerRef.current) window.clearTimeout(identifyTimerRef.current);
          setIdentifyColor(col);
          setIdentifyActive(true);
          identifyTimerRef.current = window.setTimeout(() => setIdentifyActive(false), dur);
        }
      } else if (status.type === 'control-feedback-event') {
        if (!status?.data?.data?.sessionUUID) {
          return;
        }

        if (status?.data?.data?.sessionUUID !== sessionStorage.getItem('session_uuid')) {
          return;
        }

        if (status?.data?.data?.severity === 'warning' || status?.data?.data?.severity === 'error') {
          dispatch(addModal('control-feedback-modal', status.action, { ...status.data, __toastId: Date.now() }));
        }
        if (status?.data?.data?.severity === 'info' || status?.data?.data?.severity === 'success') {
          showFeedback(status.data, {
            id: status?.data?.data?.sessionUUID + '-' + Date.now(),
            autoCloseMs: 5000
          });
        }
      }
    },
    [props.archiveMode, props.debugMode, dispatch, applyCltStatus, clearFullSnapshotRequest, decodeCltStatusPayload, handleAdditionalVariables, showFeedback, requestFullSnapshot]
  );

  useEffect(() => {
    const ws = props.websocket.websocket.websocketObject;
    ws.onmessage = onMessage;
    return () => {
      if (ws.onmessage === onMessage) ws.onmessage = null;
    };
  }, [onMessage, props.websocket.websocket.websocketObject]);
  return (
    <>
      <div>
        {/* <SideMenu
            status={this.state.status != null ? this.state.status : {}}
            current_time={this.state.current_time != null ? this.state.current_time : ''}
          /> */}
        {identifyActive && <IdentifyFrame color={identifyColor} />}
      </div>
      <ControlFeedbackContainer />
    </>
  );
}

const mapStateToProps = createStructuredSelector({
  websocket: selectWebsocket(),
  archiveMode: makeSelectArchiveMode(),
  debugMode: makeSelectDebugMode()
});

const withConnect = connect(mapStateToProps);

Websocket.propTypes = {
  dispatch: PropTypes.func,
  status: PropTypes.object,
  children: PropTypes.object,
  refreshView: PropTypes.func,
  cltStatusReceived: PropTypes.func,
  scannerStatusReceived: PropTypes.func,
  websocketObject: PropTypes.shape({
    onopen: PropTypes.func,
    onmessage: PropTypes.func,
    onclose: PropTypes.func
  }),
  archiveMode: PropTypes.bool,
  debugMode: PropTypes.bool
};

export default compose(withConnect, memo)(Websocket);
